import { type QiraMessage } from 'api3';
import { Component, OnChanges, inject, input, signal } from '@angular/core';
import { EVENT_SCHEMA, EventTrackerInternalService } from '@app/services/event-tracker-2.service';
import { EventTrackerService, QiraEventHumanReadable } from '@app/services/event-tracker.service';
import { TabService } from '@app/services/tab.service';
import { ModalService } from '@app/components/modal/modal.service';
import { FeedbackModalComponent } from '../feedback-modal/feedback-modal.component';
import { ThanksModalComponent } from '../thanks-modal/thanks-modal.component';
import Hotjar from '@hotjar/browser';
import { TranslocoPipe } from '@jsverse/transloco';
import { Button } from 'primeng/button';

@Component({
  selector: 'app-message-feedback-links',
  imports: [TranslocoPipe, Button],
  templateUrl: './message-feedback-links.component.html',
  standalone: true,
})
export class MessageFeedbackLinksComponent implements OnChanges {
  message = input<QiraMessage>();
  threadId = input.required<string>();
  okClickedValues = signal<Record<string, any>>({});

  _message?: QiraMessage;

  tabService = inject(TabService);
  modalService = inject(ModalService);
  eventTracker = inject(EventTrackerService);
  eventTrackerInternal = inject(EventTrackerInternalService);
  hotjar = Hotjar;

  ngOnChanges() {
    this._message = { ...this.message() } as QiraMessage;
  }

  public voteAnswer(voting: number) {
    const threadId = this.threadId();
    this._message = {
      ...this.message(),
      voting,
    } as QiraMessage;

    // Dislike clicked
    if (voting === 0) {
      this.openFeedbackModal();
    }

    // Like clicked
    if (voting === 1 && threadId) {
      const messageWithoutFeedback = {
        ...this._message,
        feedback: '',
      };
      this._sendVoting(messageWithoutFeedback, threadId).then(() => {
        this.openThanksModal();
      });
    }
  }

  public openThanksModal() {
    const dialogRef = this.modalService.open(ThanksModalComponent, {
      screen: 'auto',
      showCloseButton: false,
    });

    setTimeout(() => {
      this.modalService.close(dialogRef);
    }, 2000);
  }

  private openFeedbackModal() {
    const feedBackModalRef = this.modalService.open(
      FeedbackModalComponent,
      {},
      {
        screen: 'auto',
        showCloseButton: true,
        onOkButtonClick: (modalCallBack) => {
          this.okClickedValues.update((prev) => ({ ...prev, [modalCallBack.ref]: modalCallBack.event }));
          this.modalService.close(feedBackModalRef);
        },
        onClose: (ref: string | undefined) => {
          const modalRef = ref || feedBackModalRef;
          this._onFeedbackModalClose(this.okClickedValues()[modalRef]);
          this.okClickedValues.update((prev) => {
            const newValues = { ...prev };
            delete newValues[modalRef];
            return newValues;
          });
          this.modalService.close(feedBackModalRef);
        },
      },
    );
  }

  private _onFeedbackModalClose(feedback: any) {
    if (this.threadId()) {
      let votingContent: Partial<QiraMessage> = {
        id: this._message?.id,
        voting: this._message?.voting,
      };

      if (feedback) {
        votingContent = { ...votingContent, feedback };
      }

      this._sendVoting({ ...votingContent }, this.threadId()).then(() => {
        this.openThanksModal();
      });
    }
  }

  private async _sendVoting(message: Partial<QiraMessage>, threadId: string) {
    try {
      await this.tabService.updateMessage(threadId, message);

      if (this.hotjar.isReady()) {
        this.hotjar.event(`vote-message-${message.voting === 0 ? 'down' : 'up'}`);
      }

      // deprecated event tracking
      await this.eventTracker.logEvent(QiraEventHumanReadable.USER_TEXT_FEEDBACK_SEND, {
        vote: message.voting,
        thread_id: threadId,
        answer_id: message.id,
        feedback_text: message.feedback || '',
      });

      await this.eventTrackerInternal.trackEvent(EVENT_SCHEMA.CORE_PRODUCT.QA.USER_FEEDBACK_SENT, {
        vote: message.voting,
        thread_id: threadId,
        answer_id: message.id,
        feedback_text: message.feedback || '',
      });
    } catch (error) {
      console.error('[MessageFeedbackLinksComponent] Error sending voting:', error);
      // Handle the error as needed, e.g., show a notification to the user
      // this.notificationService.showError('Failed to send voting. Please try again.');
    }
  }
}
